import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AdminSocialService, SocialPostDTO } from 'src/app/services/admin-social/admin-social.service';
import { AdminConfigService } from 'src/app/services/admin-config/admin-config.service';

type Canal = 'wordpress' | 'facebook' | 'instagram' | 'linkedin';

@Component({
  selector: 'app-admin-social',
  templateUrl: './admin-social.component.html',
  styleUrls: ['./admin-social.component.scss']
})
export class AdminSocialComponent implements OnInit {

  generando = false;
  generarErr = '';
  preview: SocialPostDTO | null = null;

  publicando: Record<string, boolean> = {};
  publicarErr = '';

  historial: SocialPostDTO[] = [];
  cargandoHistorial = false;
  page = 0;
  size = 20;
  totalPages = 0;

  /** Disponibilidad real (habilitado + credenciales completas) por canal, según /admin/config. */
  channelsReady: Record<Canal, boolean> = { wordpress: false, facebook: false, instagram: false, linkedin: false };
  cargandoCanales = true;

  /** Canales que el admin quiere considerar para la publicación de este draft (checkboxes). */
  selected: Record<Canal, boolean> = { wordpress: false, facebook: false, instagram: false, linkedin: false };

  readonly canales: Canal[] = ['wordpress', 'facebook', 'instagram', 'linkedin'];

  constructor(private svc: AdminSocialService, private configSvc: AdminConfigService, private router: Router, private sanitizer: DomSanitizer) {}

  get wpHtmlSafe(): SafeHtml | null {
    if (!this.preview?.wpContentHtml) return null;
    return this.sanitizer.bypassSecurityTrustHtml(this.preview.wpContentHtml);
  }

  ngOnInit(): void {
    this.cargarCanalesDisponibles();
    this.cargarHistorial();
  }

  cargarCanalesDisponibles(): void {
    this.cargandoCanales = true;
    this.configSvc.get().subscribe({
      next: (res: any) => {
        const cfg = res?.data ?? res;
        this.channelsReady = {
          wordpress: !!cfg?.wordpressReady,
          facebook: !!cfg?.facebookReady,
          instagram: !!cfg?.instagramReady,
          linkedin: !!cfg?.linkedinReady,
        };
        // Por default selecciona todos los que ya están listos.
        this.selected = { ...this.channelsReady };
        this.cargandoCanales = false;
      },
      error: () => { this.cargandoCanales = false; }
    });
  }

  hayCanalSeleccionado(): boolean {
    return this.canales.some(c => this.selected[c] && this.channelsReady[c]);
  }

  generarVistaPrevia(): void {
    this.generando = true;
    this.generarErr = '';
    this.preview = null;
    this.svc.generarPreview().subscribe({
      next: (res: any) => {
        this.preview = res?.data ?? res;
        this.generando = false;
      },
      error: err => {
        this.generarErr = err?.error?.message ?? err?.error?.messageError ?? 'Error al generar la vista previa.';
        this.generando = false;
      }
    });
  }

  publicarEn(canal: Canal): void {
    if (!this.preview || !this.channelsReady[canal]) return;
    this.publicarUnoOTodos({
      wordpress: canal === 'wordpress',
      facebook: canal === 'facebook',
      instagram: canal === 'instagram',
      linkedin: canal === 'linkedin',
    });
  }

  /** Publica en todos los canales marcados con el checkbox y que además estén listos. */
  publicarEnSeleccionados(): void {
    if (!this.preview) return;
    this.publicarUnoOTodos({
      wordpress: this.selected.wordpress && this.channelsReady.wordpress,
      facebook: this.selected.facebook && this.channelsReady.facebook,
      instagram: this.selected.instagram && this.channelsReady.instagram,
      linkedin: this.selected.linkedin && this.channelsReady.linkedin,
    });
  }

  private publicarUnoOTodos(canales: Record<Canal, boolean>): void {
    if (!this.preview) return;
    const key = Object.entries(canales).filter(([, v]) => v).map(([k]) => k).join('+') || 'ninguno';
    if (key === 'ninguno') return;
    this.publicando[key] = true;
    this.publicarErr = '';
    this.svc.publicar(this.preview.id, canales.wordpress, canales.facebook, canales.instagram, canales.linkedin).subscribe({
      next: (res: any) => {
        this.preview = res?.data ?? res;
        this.publicando[key] = false;
        this.cargarHistorial();
      },
      error: err => {
        this.publicarErr = err?.error?.message ?? err?.error?.messageError ?? 'Error al publicar.';
        this.publicando[key] = false;
      }
    });
  }

  yaPublicado(canal: Canal): boolean {
    return !!this.preview?.[canal]?.published;
  }

  /** true si hay al menos un canal ya configurado/habilitado donde este post todavía no se publicó. */
  tienePendientes(h: SocialPostDTO): boolean {
    return this.canales.some(c => this.channelsReady[c] && !h[c]?.published);
  }

  cargarHistorial(): void {
    this.cargandoHistorial = true;
    this.svc.historial(this.page, this.size).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        this.historial = data?.content ?? [];
        this.totalPages = data?.totalPages ?? 0;
        this.cargandoHistorial = false;
      },
      error: () => { this.cargandoHistorial = false; }
    });
  }

  verDetalle(id: string): void {
    this.svc.detalle(id).subscribe({
      next: (res: any) => {
        this.preview = res?.data ?? res;
        // Al reabrir un draft del historial, muestra los mockups de todos los canales
        // listos (para no depender de qué estaba marcado antes) y lleva la vista ahí,
        // así se puede decidir publicar en los canales que falten sin regenerar nada.
        this.selected = { ...this.channelsReady };
        setTimeout(() => {
          document.getElementById('preview-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
      },
      error: () => {}
    });
  }

  cerrarSesion(): void {
    this.router.navigate(['/acceder']);
  }
}
